import React, { useMemo } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { HierarchyAPI } from "@ai360/core";

import { OrgLevelSearch } from "~/admin/agBytes/components/org-level-search";
import { ZeroToInfiniteGrid } from "~/core";

import { messages } from "../../i18n-messages";
import { model } from "../../data";

type IHierarchyFilterLevelWithParentsGuid = HierarchyAPI.IHierarchyFilterLevelWithParentsGuid;

type OrgLevelListProps = {
    orgLevels: IHierarchyFilterLevelWithParentsGuid[];
    selectedOrgLevels: IHierarchyFilterLevelWithParentsGuid[];
    onSelectionChange: (
        items: IHierarchyFilterLevelWithParentsGuid[],
        removedOrgLevel: IHierarchyFilterLevelWithParentsGuid
    ) => void;
    hasError?: boolean;
    intl: intlShape;
};

const OrgLevelList_ = ({
    orgLevels = [],
    intl,
    selectedOrgLevels,
    onSelectionChange,
    hasError = false,
}: OrgLevelListProps) => {
    const getParentGuids = (item: any): string[] => {
        const fromParents = (item?.orgLevelParents ?? [])
            .map((parent) => (typeof parent === "string" ? null : parent?.guid))
            .filter(Boolean);
        const fromParentGuids = (item?.orgLevelParentsGuids ?? []).filter(Boolean);
        return Array.from(new Set([...fromParents, ...fromParentGuids]));
    };

    const normalizeOrgLevelForSearch = (item: any) => {
        const parentNames = (item?.orgLevelParents ?? [])
            .map((parent) => {
                if (typeof parent === "string") {
                    return parent;
                }
                return parent?.name || parent?.orgLevelName || "";
            })
            .filter(Boolean);

        return {
            ...item,
            orgLevelParents: parentNames,
            orgLevelParentsGuids: getParentGuids(item),
        };
    };

    const dropdownOptions = useMemo(() => {
        return orgLevels.map((option) => normalizeOrgLevelForSearch(option));
    }, [orgLevels]);

    const selectedDisplayValues = useMemo(
        () => selectedOrgLevels.map((option) => normalizeOrgLevelForSearch(option)),
        [selectedOrgLevels]
    );

    const availableOptions = useMemo(() => {
        const selectedRecords = new Set(selectedOrgLevels.map((r) => r.orgLevelGuid));
        return dropdownOptions.filter((i) => !selectedRecords.has(i.orgLevelGuid));
    }, [selectedOrgLevels, dropdownOptions]);

    const onChange = (item: IHierarchyFilterLevelWithParentsGuid, isAdd) => {
        if (item == null) {
            return;
        }
        if (isAdd) {
            let selectedValues = [...selectedOrgLevels, item];
            const parentGuids = getParentGuids(item);
            if (parentGuids.length > 0) {
                const parentOrgLevels = new Set(parentGuids);
                selectedValues = selectedValues.filter((r) => !parentOrgLevels.has(r.orgLevelGuid));
            }
            selectedValues = selectedValues.filter(
                (r) => !getParentGuids(r).includes(item.orgLevelGuid)
            );
            onSelectionChange(selectedValues, null);
            return;
        }
        const removedOrgLevel = selectedOrgLevels.find((r) => r.orgLevelGuid === item.orgLevelGuid);
        const selectedValues = selectedOrgLevels.filter(
            (r) => r.orgLevelGuid !== item.orgLevelGuid
        );
        onSelectionChange(selectedValues, removedOrgLevel);
    };

    const { formatMessage } = intl;
    return (
        <div className={"form-section-child-stretch mini-grid auto-search-list-container"}>
            <OrgLevelSearch
                disabled={availableOptions.length === 0}
                classNames={hasError ? ["form-input-error"] : []}
                apiErrors={[]}
                clearOnSelection
                containerClassName={"org-level-search-container"}
                itemList={availableOptions}
                onSelection={(value) => onChange(value, true)}
                placeholderText={formatMessage(messages.addLocation)}
                required={true}
                selectedValue={selectedDisplayValues}
            />
            <ZeroToInfiniteGrid
                disabled={false}
                records={selectedOrgLevels}
                columns={{
                    [model.PROPS_ORG_LEVEL_NAME]: {
                        title: formatMessage(messages.name),
                        className: "org-level-name",
                    },
                    [model.PROPS_ORG_LEVEL_CITY]: {
                        title: formatMessage(messages.city),
                        className: "org-level-city",
                    },
                    [model.PROPS_ORG_LEVEL_STATE]: {
                        title: formatMessage(messages.state),
                        className: "org-level-state",
                    },
                }}
                className="org-level-grid"
                onDelete={(option) => onChange(option, false)}
            />
        </div>
    );
};

export const OrgLevelList = injectIntl(OrgLevelList_);
